import React from "react";
import type { ApolloClient } from "@apollo/client";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { connect, type ConnectedProps } from "react-redux";
import { get } from "lodash";
import { toast } from "react-toastify";
import { push } from "store/actions/router";
import ControllerForm from "pages/dashboards/adminDashboard/KYB/ControllerForm";

import { wrapError } from "utils/errorHelper";
import { registerKYBUser } from "actions/adminActions";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { adminConstants, userConstants } from "actions/types";
import { getUserState } from "actions/userActions";
import ProgressBar from "components/ProgressBar";
import "./EmployerEnroll.scss";
import {
  showsOrderBillingStepSelector,
  planCodeSelector,
} from "store/selectors/employer";
import { getPaymentRequirement } from "actions/employerActions";
import { ENTERPRISE } from "statics/planCodes";
import { COMPANY_INFO } from "statics/onboardingSteps";
import type { RootState } from "types/state";
import type { RegisterKybUserMutationVariables } from "types/graphql";

const fetchingSelector = createLoadingSelector([
  adminConstants.REGISTER_KYB_USER,
  userConstants.USER_STATE,
]);

const errorSelector = createErrorSelector(adminConstants.REGISTER_KYB_USER);

const mapStateToProps = (state: RootState) => {
  return {
    // company.id default is "" — always a string at runtime; lodash get types
    // this as unknown, so cast at the boundary
    companyId: get(state.employer, "company.id", "") as string,
    isFetching: fetchingSelector(state),
    error: errorSelector(state),
    isMultiAccount: state.employer.isMultiAccount,
    // planCodeSelector (still-inferred {} | null) yields a plan-code string or
    // null; consumed as a string here — cast at the boundary
    planCode: planCodeSelector(state) as string,
    showsOrderBillingStep: showsOrderBillingStepSelector(state),
  };
};

const mapDispatchToProps = {
  getPaymentRequirement,
  registerKYBUser,
  push,
  getUserState,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  // From the PropTypes shape: only `memberProfile` was declared, but the owner
  // record is spread wholesale into ControllerForm's initialValues, so allow
  // the additional member fields callers pass through.
  owner?: {
    memberProfile?: object;
    [key: string]: unknown;
  };
}

type Props = WithApolloClient<PropsFromRedux & OwnProps>;

class OwnerFullInfoForm extends React.PureComponent<Props> {
  componentDidMount() {
    void this.props.getPaymentRequirement(
      this.props.client as ApolloClient<object>
    );
  }

  _onSubmit = (updatedInfo: unknown) => {
    this.props
      .registerKYBUser(
        this.props.client as ApolloClient<object>,
        updatedInfo as RegisterKybUserMutationVariables
      )
      .then((result) => {
        if (!result.success) return;
        void this.props
          .getUserState(this.props.client as ApolloClient<object>)
          .then(() => {
            this.props.push(`/employer/terms`);
          });
      })
      .catch((err) => {
        toast.error(wrapError(err));
      });
  };

  render() {
    const owner = this.props.owner || {};
    const ownerInfo = {
      ...owner,
      ...owner.memberProfile,
      address1: "",
      address2: "",
      city: "",
      state: "",
      postalCode: "",
      ownershipStake: undefined,
    };

    const progPercent = this.props.isMultiAccount ? "25" : "75";

    return (
      <div>
        <ProgressBar
          isEmployer={true}
          activeStepId={COMPANY_INFO.id}
          progressPercent={progPercent}
          isMultiAccount={this.props.isMultiAccount}
          showBillingRoute={
            (!!this.props.planCode && this.props.planCode !== ENTERPRISE) ||
            this.props.showsOrderBillingStep
          }
        />
        {
          // PRESERVED BUG (conversion-only): ControllerForm requires a
          // `members: Member[]` prop, but this caller has never passed one.
          // members is only read for SSN-duplicate validation when
          // isAdminPortal is true (false here), so the omission is inert at
          // runtime; preserving the original prop set verbatim.
          // @ts-expect-error preserved bug: required `members` prop omitted
          <ControllerForm
            ownerInfo={ownerInfo}
            error={this.props.error}
            isFetching={this.props.isFetching}
            isAdminPortal={false}
            companyId={this.props.companyId}
            onSubmit={this._onSubmit}
            planCode={this.props.planCode}
          />
        }
      </div>
    );
  }
}

export default connector(withApollo<Props>(OwnerFullInfoForm));
